import React, { useState, useEffect, useMemo } from 'react';
import styles from '../AdminPanel.module.css';
import { MdEdit, MdAdd, MdDelete, MdDone, MdClose, MdRefresh, MdUpload, 
  MdSearch, MdFilterList, MdLocationOn, MdPublic, MdOutlineCode, 
  MdDirectionsBus, MdTrain, MdDirectionsBoat, MdOutlineTram, 
  MdOutlineAnnouncement, MdOutlineDirections, MdClear, MdCheck } from 'react-icons/md';
import { FirebaseCity, getAllCities, addCity, updateCity, deleteCity, 
  migrateLocalCitiesToFirebase } from '../../../services/firebase/cityServices';
import localCitiesJson from '../../../data/cities.json';

const CityManagement: React.FC = () => {
  const [cities, setCities] = useState<FirebaseCity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMigrateConfirm, setShowMigrateConfirm] = useState(false);
  const [editForm, setEditForm] = useState<Partial<FirebaseCity>>({});
  const [message, setMessage] = useState<{type: 'success' | 'error' | 'warning'; text: string} | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterTransportType, setFilterTransportType] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('cityName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  useEffect(() => {
    loadCities();
  }, []);
  
  const loadCities = async () => {
    try {
      setLoading(true);
      const fetchedCities = await getAllCities();
      setCities(fetchedCities);
      setMessage({
        type: 'success',
        text: `${fetchedCities.length} şehir başarıyla yüklendi.`
      });
    } catch (error) {
      console.error('Şehirler yüklenirken hata:', error);
      setMessage({
        type: 'error',
        text: 'Şehirler yüklenirken bir hata oluştu!'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditClick = (city: FirebaseCity) => {
    setEditMode(city.id || null);
    setEditForm(city);
  };
  
  const handleCancelEdit = () => {
    setEditMode(null);
    setEditForm({});
  };
  
  const handleSaveEdit = async () => {
    if (!editForm.id) return;
    
    try {
      setLoading(true);
      
      const updateData: any = {
        cityName: editForm.cityName,
        countryName: editForm.countryName,
        countryCode: editForm.countryCode,
        cityCode: editForm.cityCode,
        apiEndpoint: editForm.apiEndpoint,
        isActive: editForm.isActive,
        features: editForm.features,
        routePlanning: editForm.routePlanning,
        announcements: editForm.announcements,
        description: editForm.description,
        dataSource: editForm.dataSource,
        dataSourceUrl: editForm.dataSourceUrl,
      };
      
      if (editForm.location && 
          typeof editForm.location.latitude === 'number' && 
          typeof editForm.location.longitude === 'number') {
        updateData.location = {
          latitude: editForm.location.latitude,
          longitude: editForm.location.longitude
        };
      }
      
      await updateCity(editForm.id, updateData);
      
      await loadCities();
      
      setMessage({
        type: 'success',
        text: `${editForm.cityName} şehri başarıyla güncellendi.`
      });
      
      setEditMode(null);
      setEditForm({});
    } catch (error) {
      console.error('Şehir güncellenirken hata:', error);
      setMessage({
        type: 'error',
        text: 'Şehir güncellenirken bir hata oluştu!'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCity = async (cityId: string | undefined, cityName: string) => {
    if (!cityId) {
      setMessage({
        type: 'error',
        text: 'Şehir ID\'si bulunamadı!'
      });
      return;
    }
    
    if (!window.confirm(`${cityName} şehrini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteCity(cityId);
      
      setCities(prev => prev.filter(city => city.id !== cityId));
      
      setMessage({
        type: 'success',
        text: `${cityName} şehri başarıyla silindi.`
      });
    } catch (error) {
      console.error('Şehir silinirken hata:', error);
      setMessage({
        type: 'error',
        text: 'Şehir silinirken bir hata oluştu!'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddNewCity = async () => {
    if (!editForm.cityName || !editForm.cityCode || !editForm.apiEndpoint) {
      setMessage({
        type: 'warning',
        text: 'Lütfen tüm gerekli alanları doldurun'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const newCityData: any = {
        countryCode: editForm.countryCode || 'TR',
        countryName: editForm.countryName || 'Türkiye',
        cityCode: editForm.cityCode || '',
        cityName: editForm.cityName || '',
        isActive: editForm.isActive || false,
        apiEndpoint: editForm.apiEndpoint || '',
        features: {
          bus: editForm.features?.bus || false,
          metro: editForm.features?.metro || false,
          tram: editForm.features?.tram || false,
          ferry: editForm.features?.ferry || false,
          train: editForm.features?.train || false
        },
        routePlanning: editForm.routePlanning || false,
        announcements: editForm.announcements || false,
        description: editForm.description || '',
        dataSource: editForm.dataSource || '',
        dataSourceUrl: editForm.dataSourceUrl || '',
      };
      
      if (editForm.location && 
          typeof editForm.location.latitude === 'number' && 
          typeof editForm.location.longitude === 'number') {
        newCityData.location = {
          latitude: editForm.location.latitude,
          longitude: editForm.location.longitude
        };
      }
      
      await addCity(newCityData);
      
      await loadCities();
      
      setMessage({
        type: 'success',
        text: `${editForm.cityName} şehri başarıyla eklendi.`
      });
      
      setShowAddForm(false);
      setEditForm({});
    } catch (error) {
      console.error('Şehir eklenirken hata:', error);
      setMessage({
        type: 'error',
        text: 'Şehir eklenirken bir hata oluştu!'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      
      if (name.startsWith('features.')) {
        const featureName = name.split('.')[1] as keyof typeof editForm.features;
        
        setEditForm(prev => ({
          ...prev,
          features: {
            ...(prev.features || { bus: false, metro: false, tram: false, ferry: false, train: false }),
            [featureName]: checkbox.checked
          }
        }));
      } else {
        setEditForm(prev => ({
          ...prev,
          [name]: checkbox.checked
        }));
      }
    } else if (name.startsWith('location.')) {
      const locationKey = name.split('.')[1] as keyof FirebaseCity['location'];
      const numValue = value ? Number(value) : undefined;
      
      setEditForm(prev => {
        const currentLocation = prev.location || { latitude: undefined, longitude: undefined };
        const updatedLocation = {
          ...currentLocation,
          [locationKey]: numValue
        };
        return {
          ...prev,
          location: updatedLocation
        };
      });
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleMigrateData = async () => {
    try {
      setLoading(true);
      await migrateLocalCitiesToFirebase(localCitiesJson.cities);
      
      setMessage({
        type: 'success',
        text: `Veriler başarıyla Firebase veritabanına aktarıldı.`
      });
      
      await loadCities();
      setShowMigrateConfirm(false);
    } catch (error) {
      console.error('Veri aktarımı sırasında hata:', error);
      setMessage({
        type: 'error',
        text: 'Veri aktarımı sırasında bir hata oluştu!'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterActive(null);
    setFilterTransportType(null);
  };

  const filteredAndSortedCities = useMemo(() => {
    let result = [...cities];
    
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(city => 
        city.cityName.toLowerCase().includes(searchLower) || 
        city.cityCode.toLowerCase().includes(searchLower) ||
        city.countryName.toLowerCase().includes(searchLower)
      );
    }
    
    if (filterActive !== null) {
      result = result.filter(city => city.isActive === filterActive);
    }
    
    if (filterTransportType) {
      switch (filterTransportType) {
        case 'bus':
          result = result.filter(city => city.features?.bus);
          break;
        case 'metro':
          result = result.filter(city => city.features?.metro);
          break;
        case 'tram':
          result = result.filter(city => city.features?.tram);
          break;
        case 'ferry':
          result = result.filter(city => city.features?.ferry);
          break;
        case 'train':
          result = result.filter(city => city.features?.train);
          break;
        case 'routePlanning':
          result = result.filter(city => city.routePlanning);
          break;
        case 'announcements':
          result = result.filter(city => city.announcements);
          break;
        default:
          break;
      }
    }
    
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'cityName':
          comparison = a.cityName.localeCompare(b.cityName);
          break;
        case 'countryName':
          comparison = a.countryName.localeCompare(b.countryName);
          break;
        case 'cityCode':
          comparison = a.cityCode.localeCompare(b.cityCode);
          break;
        case 'updatedAt':
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          comparison = dateA - dateB;
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [cities, searchTerm, filterActive, filterTransportType, sortBy, sortDirection]);

  const [showFilters, setShowFilters] = useState(false);
  return (
    <div>
      <h2 className={styles.sectionTitle}>Şehir Yönetimi</h2>

      {message && (
        <div className={styles[message.type]}>
          <p>{message.text}</p>
          <button
            className={styles.closeButton}
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.cardSection}>
        <div className={styles.sectionHeader}>
          <h3><MdPublic /> Mevcut Şehirler</h3>
          <div className={styles.headerActions}>
            <button
              className={styles.migrateButton}
              onClick={() => setShowMigrateConfirm(true)}
              title="Yerel JSON dosyasındaki verileri Firebase'e aktarır"
            >
              <MdUpload /> Yerel JSON'dan Aktarım
            </button>
            <button
              className={styles.refreshButton}
              onClick={loadCities}
              title="Şehir listesini yeniler"
            >
              <MdRefresh /> Yenile
            </button>
            <button
              className={styles.addButton}
              onClick={() => {
                setEditForm({});
                setShowAddForm(true);
              } }
              title="Yeni bir şehir ekler"
            >
              <MdAdd /> Yeni Şehir Ekle
            </button>
          </div>
        </div>

        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <MdSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Şehir adı, kodu veya ülke adı ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && (
              <button
                className={styles.clearSearchButton}
                onClick={() => setSearchTerm('')}
              >
                <MdClear />
              </button>
            )}
          </div>

          <button
            className={`${styles.filterButton} ${showFilters ? styles.activeFilterButton : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList /> Filtreler
          </button>
        </div>

        {showFilters && (
          <div className={styles.filtersContainer}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Durum:</label>
              <select
                className={styles.filterSelect}
                value={filterActive === null ? '' : filterActive ? 'active' : 'inactive'}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setFilterActive(null);
                  } else {
                    setFilterActive(e.target.value === 'active');
                  }
                } }
              >
                <option value="">Tümü</option>
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Ulaşım Tipi:</label>
              <select
                className={styles.filterSelect}
                value={filterTransportType || ''}
                onChange={(e) => setFilterTransportType(e.target.value || null)}
              >
                <option value="">Tümü</option>
                <option value="bus">Otobüs</option>
                <option value="metro">Metro</option>
                <option value="tram">Tramvay</option>
                <option value="ferry">Vapur</option>
                <option value="train">Tren</option>
                <option value="routePlanning">Yol Tarifi</option>
                <option value="announcements">Duyurular</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Sırala:</label>
              <select
                className={styles.filterSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="cityName">Şehir Adı</option>
                <option value="countryName">Ülke Adı</option>
                <option value="cityCode">Şehir Kodu</option>
                <option value="updatedAt">Son Güncelleme</option>
              </select>
              <button
                className={styles.sortDirectionButton}
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            </div>

            {(searchTerm || filterActive !== null || filterTransportType) && (
              <button
                className={styles.clearFiltersButton}
                onClick={clearFilters}
              >
                <MdClear /> Filtreleri Temizle
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Şehirler yükleniyor...</p>
          </div>
        ) : filteredAndSortedCities.length === 0 ? (
          <div className={styles.emptyState}>
            {searchTerm || filterActive !== null || filterTransportType ? (
              <>
                <p>Arama kriterlerinize uygun şehir bulunamadı.</p>
                <button
                  className={styles.clearFiltersButton}
                  onClick={clearFilters}
                >
                  Filtreleri Temizle
                </button>
              </>
            ) : (
              <p>Henüz kayıtlı şehir bulunmuyor.</p>
            )}
          </div>
        ) : (
          <>
            <div className={styles.citySummary}>
              <p>Toplam: <strong>{filteredAndSortedCities.length}</strong> şehir {(searchTerm || filterActive !== null || filterTransportType) ? '(filtrelenmiş)' : ''}</p>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('cityCode')} className={styles.sortableHeader}>
                      Şehir Kodu {sortBy === 'cityCode' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('cityName')} className={styles.sortableHeader}>
                      Şehir {sortBy === 'cityName' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>API Endpoint</th>
                    <th>Özellikler</th>
                    <th>Durum</th>
                    <th onClick={() => handleSort('updatedAt')} className={styles.sortableHeader}>
                      Son Güncelleme {sortBy === 'updatedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCities.map(city => (
                    <tr key={city.id}>
                      <td>{city.cityCode}</td>
                      <td>
                        <div className={styles.cityInfo}>
                          <span>{city.cityName}</span>
                          <small>{city.countryName}</small>
                          {city.location && (
                            <small className={styles.locationInfo}>
                              <MdLocationOn /> {city.location.latitude?.toFixed(4)}, {city.location.longitude?.toFixed(4)}
                            </small>
                          )}
                        </div>
                      </td>
                      <td className={styles.apiEndpoint} title={city.apiEndpoint}>{city.apiEndpoint}</td>
                      <td>
                        <div className={styles.featuresList}>
                          {city.features?.bus && <span className={styles.featureTag} title="Otobüs"><MdDirectionsBus /></span>}
                          {city.features?.metro && <span className={styles.featureTag} title="Metro"><MdTrain /></span>}
                          {city.features?.tram && <span className={styles.featureTag} title="Tramvay"><MdOutlineTram /></span>}
                          {city.features?.ferry && <span className={styles.featureTag} title="Vapur"><MdDirectionsBoat /></span>}
                          {city.features?.train && <span className={styles.featureTag} title="Tren"><MdTrain /></span>}
                          {city.routePlanning && <span className={styles.featureTag} title="Yol Tarifi"><MdOutlineDirections /></span>}
                          {city.announcements && <span className={styles.featureTag} title="Duyurular"><MdOutlineAnnouncement /></span>}
                        </div>
                      </td>
                      <td>
                        <span className={city.isActive ? styles.activeBadge : styles.inactiveBadge}>
                          {city.isActive ? <><MdCheck /> Aktif</> : <><MdClose /> Pasif</>}
                        </span>
                      </td>
                      <td>{formatDate(city.updatedAt)}</td>
                      <td>
                        <div className={styles.actionButtons}>
                          {editMode === city.id ? (
                            <>
                              <button
                                className={styles.saveButton}
                                onClick={handleSaveEdit}
                                title="Değişiklikleri kaydet"
                              >
                                <MdDone />
                              </button>
                              <button
                                className={styles.cancelButton}
                                onClick={handleCancelEdit}
                                title="Düzenlemeyi iptal et"
                              >
                                <MdClose />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={styles.editButton}
                                onClick={() => handleEditClick(city)}
                                title="Şehri düzenle"
                              >
                                <MdEdit />
                              </button>
                              <button
                                className={styles.deleteButton}
                                onClick={() => handleDeleteCity(city.id, city.cityName)}
                                title="Şehri sil"
                              >
                                <MdDelete />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showAddForm && (
        <div className={styles.cardSection}>
          <h3><MdAdd /> Yeni Şehir Ekle</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Şehir Adı*</label>
              <input
                type="text"
                name="cityName"
                className={styles.formInput}
                value={editForm.cityName || ''}
                onChange={handleInputChange}
                required />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Şehir Kodu*</label>
              <input
                type="text"
                name="cityCode"
                className={styles.formInput}
                value={editForm.cityCode || ''}
                onChange={handleInputChange}
                required />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Ülke Adı</label>
              <input
                type="text"
                name="countryName"
                className={styles.formInput}
                value={editForm.countryName || 'Türkiye'}
                onChange={handleInputChange} />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Ülke Kodu</label>
              <input
                type="text"
                name="countryCode"
                className={styles.formInput}
                value={editForm.countryCode || 'TR'}
                onChange={handleInputChange} />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>API Endpoint*</label>
              <input
                type="text"
                name="apiEndpoint"
                className={styles.formInput}
                value={editForm.apiEndpoint || ''}
                onChange={handleInputChange}
                required />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={editForm.isActive || false}
                  onChange={handleInputChange} />
                Aktif
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Açıklama</label>
              <textarea
                name="description"
                className={styles.formTextarea}
                value={editForm.description || ''}
                onChange={handleInputChange} />
            </div>
          </div>

          <h4><MdLocationOn /> Konum</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Enlem</label>
              <input
                type="number"
                name="location.latitude"
                className={styles.formInput}
                value={editForm.location?.latitude ?? ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="Örn: 41.0082" />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Boylam</label>
              <input
                type="number"
                name="location.longitude"
                className={styles.formInput}
                value={editForm.location?.longitude ?? ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="Örn: 28.9784" />
            </div>
          </div>

          <h4><MdDirectionsBus /> Özellikler</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="features.bus"
                  checked={editForm.features?.bus || false}
                  onChange={handleInputChange} />
                <MdDirectionsBus /> Otobüs
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="features.metro"
                  checked={editForm.features?.metro || false}
                  onChange={handleInputChange} />
                <MdTrain /> Metro
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="features.tram"
                  checked={editForm.features?.tram || false}
                  onChange={handleInputChange} />
                <MdOutlineTram /> Tramvay
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="features.ferry"
                  checked={editForm.features?.ferry || false}
                  onChange={handleInputChange} />
                <MdDirectionsBoat /> Vapur
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="features.train"
                  checked={editForm.features?.train || false}
                  onChange={handleInputChange} />
                <MdTrain /> Tren
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="routePlanning"
                  checked={editForm.routePlanning || false}
                  onChange={handleInputChange} />
                <MdOutlineDirections /> Yol Tarifi
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="announcements"
                  checked={editForm.announcements || false}
                  onChange={handleInputChange} />
                <MdOutlineAnnouncement /> Duyurular
              </label>
            </div>
          </div>

          <h4><MdOutlineCode /> Veri Kaynağı</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Veri Kaynağı</label>
              <input
                type="text"
                name="dataSource"
                className={styles.formInput}
                value={editForm.dataSource || ''}
                onChange={handleInputChange}
                placeholder="Örn: İBB Açık Veri Portalı" />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Veri Kaynağı URL</label>
              <input
                type="text"
                name="dataSourceUrl"
                className={styles.formInput}
                value={editForm.dataSourceUrl || ''}
                onChange={handleInputChange}
                placeholder="Örn: https://data.ibb.gov.tr" />
            </div>
          </div>

          <div className={styles.formActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setShowAddForm(false)}
            >
              İptal
            </button>
            <button
              className={styles.submitButton}
              onClick={handleAddNewCity}
            >
              <MdAdd /> Şehir Ekle
            </button>
          </div>
        </div>
      )}

      {showMigrateConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}><MdUpload /> Veri Aktarımı Onayı</h3>
            <div className={styles.modalBody}>
              <p>
                Yerel JSON dosyasındaki şehir verilerini Firebase veritabanına aktarmak istediğinizden emin misiniz?
              </p>
              <div className={styles.warningBox}>
                <p><strong>Dikkat:</strong> Bu işlem, eski verilerin üzerine yazabilir. Devam etmeden önce mevcut verilerin yedeğini almanızı öneririz.</p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowMigrateConfirm(false)}
              >
                İptal
              </button>
              <button
                className={styles.modalSubmitButton}
                onClick={handleMigrateData}
              >
                <MdUpload /> Aktarımı Başlat
              </button>
            </div>
          </div>
        </div>
      )}

    {editMode && (
      <div className={styles.cardSection}>
        <h3><MdEdit /> {editForm.cityName} Şehrini Düzenle</h3>
        
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Şehir Adı*</label>
            <input
              type="text"
              name="cityName"
              className={styles.formInput}
              value={editForm.cityName || ''}
              onChange={handleInputChange}
              required />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Şehir Kodu*</label>
            <input
              type="text"
              name="cityCode"
              className={styles.formInput}
              value={editForm.cityCode || ''}
              onChange={handleInputChange}
              required />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Ülke Adı</label>
            <input
              type="text"
              name="countryName"
              className={styles.formInput}
              value={editForm.countryName || 'Türkiye'}
              onChange={handleInputChange} />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Ülke Kodu</label>
            <input
              type="text"
              name="countryCode"
              className={styles.formInput}
              value={editForm.countryCode || 'TR'}
              onChange={handleInputChange} />
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>API Endpoint*</label>
            <input 
              type="text"
              name="apiEndpoint"
              className={styles.formInput}
              value={editForm.apiEndpoint || ''}
              onChange={handleInputChange}
              required
            />
          </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="isActive"
                  checked={editForm.isActive || false}
                  onChange={handleInputChange}
                />
                Aktif
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Açıklama</label>
              <textarea 
                name="description"
                className={styles.formTextarea}
                value={editForm.description || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <h4><MdLocationOn /> Konum</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Enlem</label>
              <input 
                type="number"
                name="location.latitude"
                className={styles.formInput}
                value={editForm.location?.latitude ?? ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="Örn: 41.0082"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Boylam</label>
              <input 
                type="number"
                name="location.longitude"
                className={styles.formInput}
                value={editForm.location?.longitude ?? ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="Örn: 28.9784"
              />
            </div>
          </div>
            <h4><MdDirectionsBus /> Özellikler</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="features.bus"
                  checked={editForm.features?.bus || false}
                  onChange={handleInputChange}
                />
                <MdDirectionsBus /> Otobüs
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="features.metro"
                  checked={editForm.features?.metro || false}
                  onChange={handleInputChange}
                />
                <MdTrain /> Metro
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="features.tram"
                  checked={editForm.features?.tram || false}
                  onChange={handleInputChange}
                />
                <MdOutlineTram /> Tramvay
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="features.ferry"
                  checked={editForm.features?.ferry || false}
                  onChange={handleInputChange}
                />
                <MdDirectionsBoat /> Vapur
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="features.train"
                  checked={editForm.features?.train || false}
                  onChange={handleInputChange}
                />
                <MdTrain /> Tren
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="routePlanning"
                  checked={editForm.routePlanning || false}
                  onChange={handleInputChange}
                />
                <MdOutlineDirections /> Yol Tarifi
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  name="announcements"
                  checked={editForm.announcements || false}
                  onChange={handleInputChange}
                />
                <MdOutlineAnnouncement /> Duyurular
              </label>
            </div>
          </div>
          
          <h4><MdOutlineCode /> Veri Kaynağı</h4>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Veri Kaynağı</label>
              <input 
                type="text"
                name="dataSource"
                className={styles.formInput}
                value={editForm.dataSource || ''}
                onChange={handleInputChange}
                placeholder="Örn: İBB Açık Veri Portalı"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Veri Kaynağı URL</label>
              <input 
                type="text"
                name="dataSourceUrl"
                className={styles.formInput}
                value={editForm.dataSourceUrl || ''}
                onChange={handleInputChange}
                placeholder="Örn: https://data.ibb.gov.tr"
              />
            </div>
          </div>
            <div className={styles.formActions}>
            <button 
              className={styles.cancelButton}
              onClick={handleCancelEdit}
            >
              <MdClose /> İptal
            </button>
            <button 
              className={styles.submitButton}
              onClick={handleSaveEdit}
            >
              <MdDone /> Değişiklikleri Kaydet
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityManagement;
